import React, { useState, useEffect } from 'react';
import { Utensils, Image as ImageIcon, FileText } from 'lucide-react';
import { cn } from '@/lib/utils';

export type SafeImageFallbackType = 'restaurantCover' | 'restaurantAvatar' | 'menuThumb' | 'gallery';

// Placeholder image für Restaurant-Header (aus /public)
const PLACEHOLDER_HEADER_URL = '/placeholder_header.png';

interface SafeImageProps {
  src?: string | null;
  alt: string;
  className?: string;
  fallbackType?: SafeImageFallbackType;
  aspectRatio?: '16:9' | '4:3' | '1:1' | 'auto';
  // Optional: Initialen für Placeholder (z.B. Restaurant-Name)
  initials?: string;
  // Optional: Custom Fallback-URL (falls vorhanden)
  fallbackSrc?: string;
  loading?: 'lazy' | 'eager';
  decoding?: 'async' | 'sync' | 'auto';
  // Ob echtes Placeholder-Bild verwendet werden soll (statt Gradient)
  usePlaceholderImage?: boolean;
}

/**
 * P0: SafeImage - Zentrale Komponente für robuste Bildanzeige
 * 
 * Features:
 * - Automatischer Fallback bei fehlenden/kaputten Bildern
 * - Kein Layout Shift (feste Aspect Ratios)
 * - Premium Placeholder-Design oder echtes Placeholder-Bild
 * - Kein "broken icon flash"
 */
export const SafeImage: React.FC<SafeImageProps> = ({
  src,
  alt,
  className = '',
  fallbackType = 'restaurantCover',
  aspectRatio = 'auto',
  initials,
  fallbackSrc,
  loading = 'lazy',
  decoding = 'async',
  usePlaceholderImage = true, // Default: verwende echtes Placeholder-Bild
}) => {
  const [hasError, setHasError] = useState(false);
  const [currentSrc, setCurrentSrc] = useState<string | null>(null);

  // P0: Reset error state wenn src sich ändert
  useEffect(() => {
    // Prüfe ob src leer/ungültig ist
    const isValidSrc = src && src.trim() !== '' && src !== 'null' && src !== 'undefined';
    
    if (!isValidSrc) {
      setHasError(true);
      setCurrentSrc(null);
    } else {
      setHasError(false);
      setCurrentSrc(src);
    }
  }, [src]);

  // Placeholder-Design basierend auf Typ
  const getPlaceholderStyle = () => {
    const baseGradient = 'bg-gradient-to-br';
    
    switch (fallbackType) {
      case 'restaurantCover':
        return `${baseGradient} from-primary/30 via-primary/20 to-gray-200`;
      case 'restaurantAvatar':
        return `${baseGradient} from-primary/40 via-primary/30 to-primary/20`;
      case 'menuThumb':
        return `${baseGradient} from-blue-400/30 via-blue-300/20 to-gray-200`;
      case 'gallery':
        return `${baseGradient} from-gray-300 via-gray-200 to-gray-100`;
      default:
        return `${baseGradient} from-gray-300 via-gray-200 to-gray-100`;
    }
  };

  // Icon basierend auf Typ
  const getPlaceholderIcon = () => {
    switch (fallbackType) {
      case 'restaurantCover':
      case 'restaurantAvatar':
        return <Utensils className="w-8 h-8 text-white/80" />;
      case 'menuThumb':
        return <FileText className="w-8 h-8 text-white/80" />;
      case 'gallery':
        return <ImageIcon className="w-8 h-8 text-white/80" />;
      default:
        return <ImageIcon className="w-8 h-8 text-white/80" />;
    }
  };

  // Aspect Ratio Container
  const getAspectRatioClass = () => {
    switch (aspectRatio) {
      case '16:9':
        return 'aspect-video';
      case '4:3':
        return 'aspect-[4/3]';
      case '1:1':
        return 'aspect-square';
      case 'auto':
      default:
        return '';
    }
  };

  // Handle Image Error
  const handleError = () => {
    // Wenn fallbackSrc vorhanden und noch nicht verwendet, versuche diesen
    if (fallbackSrc && currentSrc !== fallbackSrc) {
      setCurrentSrc(fallbackSrc);
      return;
    }
    
    // Ansonsten zeige Placeholder
    setHasError(true);
    setCurrentSrc(null);
  };

  // Handle Image Load Success
  const handleLoad = () => {
    setHasError(false);
  };

  // Wenn kein src oder Error: zeige Placeholder
  if (hasError || !currentSrc) {
    // Für restaurantCover: zeige echtes Placeholder-Bild wenn aktiviert
    if (usePlaceholderImage && (fallbackType === 'restaurantCover' || fallbackType === 'gallery')) {
      return (
        <div className={cn('relative overflow-hidden', getAspectRatioClass(), className)}>
          <img
            src={PLACEHOLDER_HEADER_URL}
            alt={alt}
            loading={loading}
            decoding={decoding}
            className="w-full h-full object-cover"
          />
        </div>
      );
    }
    
    // Gradient-Fallback für andere Typen
    return (
      <div
        className={cn(
          'relative flex items-center justify-center overflow-hidden',
          getPlaceholderStyle(),
          getAspectRatioClass(),
          className
        )}
        role="img"
        aria-label={alt}
      >
        {/* Subtile Pattern-Overlay für Premium-Look */}
        <div className="absolute inset-0 opacity-10">
          <div className="w-full h-full bg-[radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.3)_1px,transparent_0)] bg-[length:20px_20px]"></div>
        </div>
        
        {/* Icon */}
        <div className="relative z-10 flex flex-col items-center justify-center">
          {getPlaceholderIcon()}
          
          {/* Initialen (optional) */}
          {initials && initials.length > 0 && (
            <div className="mt-2 text-white/90 font-semibold text-sm">
              {initials.slice(0, 2).toUpperCase()}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Rendere Bild mit Error-Handling
  return (
    <div className={cn('relative overflow-hidden', getAspectRatioClass(), className)}>
      <img
        src={currentSrc}
        alt={alt}
        loading={loading}
        decoding={decoding}
        className="w-full h-full object-cover"
        onError={handleError}
        onLoad={handleLoad}
      />
    </div>
  );
};

export default SafeImage;
